import React, { lazy, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { CircularProgress, Box } from '@mui/material'
import { theme } from './theme'
import './App.css'
import ErrorBoundary from './components/common/ErrorBoundary'
import PrivateRoute from './route/PrivateRoute'
import PublicRoute from './route/PublicRoute'

// Eager load critical routes
import Login from './components/auth/Login'
import Home from './components/home/Home'

// Lazy load heavy components for code splitting
const Transactions = lazy(() => import('./components/transactions/Transactions'))
const CampaignsTable = lazy(() => import('./components/campaignsTable/CampaignsTable'))
const InfluencerList = lazy(() => import('./features/influencers/InfluencerList').then(m => ({ default: m.InfluencerList })))
const Dashboard = lazy(() => import('./features/dashboard/Dashboard'))
const Chat = lazy(() => import('./components/chat/Chat'))
const VideoEditing = lazy(() => import('./components/videoEditing/VideoEditing').then(m => ({ default: m.VideoEditing })))
const AcceptContent = lazy(() => import('./components/acceptContent/AcceptContent').then(m => ({ default: m.AcceptContent })))
const InternalFeedback = lazy(() => import('./components/internalFeedback/InternalFeedback').then(m => ({ default: m.InternalFeedback })))
const NewInfluencerList = lazy(() => import('./components/influencers/verification/NewInfluencerList'))
const TodoOverview = lazy(() => import('./components/todoOverview/TodoOverview'))
const Credit = lazy(() => import('./components/influencers/credit/Credit'))
const Budget = lazy(() => import('./components/brands/budget/Budget'))
const BrandsList = lazy(() => import('./components/brands/BrandsList'))
const InvoiceList = lazy(() => import('./features/influencers/InvoiceList').then(m => ({ default: m.InvoiceList })))
const InfluencerCampaigns = lazy(() => import('./features/influencers/InfluencerCampaigns'))

// Loading component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh'
    }}
  >
    <CircularProgress />
  </Box>
)

export const App = () => (
  <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path='/login' element={<PublicRoute childComponent={<Login />} />} />
          <Route path='/home' element={<PrivateRoute childComponent={<Home />} />} />
          <Route path='/campaigns' element={<PrivateRoute childComponent={<CampaignsTable />} />} />
          <Route path='/content-check' element={<PrivateRoute childComponent={<AcceptContent />} />} />
          <Route path='/influencers' element={<PrivateRoute childComponent={<InfluencerList />} />} />
          <Route path='/influencers/campaigns' element={<PrivateRoute childComponent={<InfluencerCampaigns />} />} />
          <Route path='/invoices' element={<PrivateRoute childComponent={<InvoiceList />} />} />
          <Route path='/influencers-new' element={<PrivateRoute childComponent={<NewInfluencerList />} />} />
          <Route path='/todo-overview' element={<PrivateRoute childComponent={<TodoOverview />} />} />
          <Route path='/transactions' element={<PrivateRoute childComponent={<Transactions />} />} />
          <Route path='/chat/:influencerId?' element={<PrivateRoute childComponent={<Chat />} />} />
          <Route path='/video-editing' element={<PrivateRoute childComponent={<VideoEditing />} />} />
          <Route path='/internal-feedback' element={<PrivateRoute childComponent={<InternalFeedback />} />} />
          <Route path='/credit' element={<PrivateRoute childComponent={<Credit />} />} />
          <Route path='/brands' element={<PrivateRoute childComponent={<BrandsList />} />} />
          <Route path='/budget' element={<PrivateRoute childComponent={<Budget />} />} />
          <Route path='/dashboard' element={<PrivateRoute childComponent={<Dashboard />} />} />
          <Route path='*' element={<Navigate to='/home' />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
  </ErrorBoundary>
)
